import { ReactNode } from "react"

import styled from "styled-components"

import { ReactComponent as ArrowSVG } from "images/global/arrow.svg"
import colors from "styles/colors"
import easing from "styles/easing"
import media from "styles/media"
import text from "styles/text"
import UniversalLink from "utils/Loader/UniversalLink"

interface Gradient {
  gradient: string
  fill: string
}

export default function LinkButton({
  blue = false,
  gradientBlue = false,
  gradientMagenta = false,
  gradientTurq = false,
  magenta = false,
  flip = false,
  link = "",
  hovered = false,
  children,
  gradient = undefined,
  solidColor = undefined,
  asDiv = false,
}: {
  blue?: boolean
  gradientBlue?: boolean
  gradientMagenta?: boolean
  gradientTurq?: boolean
  magenta?: boolean
  flip?: boolean
  children: ReactNode
  link?: string | null
  gradient?: Gradient
  hovered?: boolean
  solidColor?: string
  asDiv?: boolean
}) {
  let colorObject: {
    text?: string
    arrow?: string
    arrowFlipped?: string
    line?: string
  } = {}

  // a bottom line needs to be a linear-gradient even if its a solid color, such as in case blue
  switch (true) {
    case solidColor !== undefined:
      colorObject = {
        text: solidColor,
        arrow: solidColor,
        arrowFlipped: solidColor,
        line: `linear-gradient(90deg, ${solidColor ?? "black"}, ${
          solidColor ?? "black"
        });`,
      }
      break
    case gradient !== undefined: // support for existing "gradient" prop
      colorObject = {
        text: gradient?.gradient,
        arrow: gradient?.fill,
        arrowFlipped: colors.magenta400,
        line: gradient?.gradient,
      }
      break
    case magenta:
      colorObject = {
        text: colors.magenta400,
        arrow: colors.magenta400,
        arrowFlipped: colors.magenta400,
        line: `linear-gradient(90deg, ${colors.magenta400}, ${colors.magenta400});`,
      }
      break
    case blue:
      colorObject = {
        text: colors.blue400,
        arrow: colors.blue400,
        arrowFlipped: colors.blue400,
        line: `linear-gradient(90deg, ${colors.blue400}, ${colors.blue400});`,
      }
      break
    case gradientBlue:
      colorObject = {
        text: colors.blueBabyHorz,
        arrow: colors.blue400,
        arrowFlipped: colors.blue400,
        line: colors.blueBabyHorz,
      }
      break
    case gradientTurq:
      colorObject = {
        text: colors.turqBlueHorz,
        arrow: colors.turq400,
        arrowFlipped: colors.turq400,
        line: colors.turqSeaHorz,
      }
      break
    case gradientMagenta:
      colorObject = {
        text: colors.magenta400,
        arrow: colors.magenta400,
        arrowFlipped: colors.magenta400,
        line: `linear-gradient(90deg, ${colors.magenta400}, ${colors.magenta400});`,
      }

      break

    default:
      break
  }

  return asDiv ? (
    <Wrapper
      color={gradient?.gradient}
      fill={gradient?.fill}
      hovered={hovered}
      flip={flip}
      aria-label={`read more about ${String(children)}`}
      arrowColor={flip ? colorObject.arrowFlipped : colorObject.arrow}
    >
      <Row flip={flip} lineColor={colorObject.line}>
        <Text textColor={colorObject.text}>{children}</Text>
        <ArrowSVG />
      </Row>
    </Wrapper>
  ) : (
    <UniversalLink to={link ?? ""}>
      <Wrapper
        color={gradient?.gradient}
        fill={gradient?.fill}
        hovered={hovered}
        flip={flip}
        aria-label={`read more about ${String(children)}`}
        arrowColor={flip ? colorObject.arrowFlipped : colorObject.arrow}
      >
        <Row flip={flip} lineColor={colorObject.line}>
          <u color={colorObject.text}>
          <Text textColor={colorObject.text}>{children}</Text>
          {!flip?<ArrowSVG />:<></>}
          </u>
          {flip?<ArrowSVG />:<></>}
        </Row>
      </Wrapper>
    </UniversalLink>
  )
}

const Wrapper = styled.div<{
  hovered?: boolean
  fill?: string
  flip?: boolean
  arrowColor?: string
}>`
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  flex-direction: column;
  border: none;
  width: fit-content;
  transition-timing-function: ${easing.button};
  transition-duration: 300ms;

  ${media.fullWidth} {
    margin-right: 5px;
    margin-left: 5px;
  }
  ${media.desktop} {
    margin-right: 0.301vw;
    margin-left: 0.301vw;
  }
  ${media.tablet} {
    margin-right: 0.488vw;
    margin-left: 0.488vw;
  }
  ${media.mobile} {
    margin-right: 1.333vw;
    margin-left: 1.333vw;
  }
  ${props =>
    props.flip ? "margin-right: 0 !important;" : "margin-left: 0 !important;"}
  ${media.hover} {
    :hover {
      margin: 0;
    }
  }

  svg {
    transition-duration: 300ms;
    ${media.fullWidth} {
      width: 15px;
      height: 15px;
      margin-left: 10px;
      margin-right: 10px;
    }
    ${media.desktop} {
      width: 0.904vw;
      height: 0.904vw;
      margin-left: 0.602vw;
      margin-right: 0.602vw;
    }
    ${media.tablet} {
      width: 1.465vw;
      height: 1.465vw;
      margin-left: 0.977vw;
      margin-right: 0.977vw;
    }
    ${media.mobile} {
      width: 4vw;
      height: 4vw;
      margin-left: 2.667vw;
      margin-right: 2.667vw;
    }
    height: auto;
    transition-timing-function: ${easing.button};
    ${props => (props.flip ? "transform: rotate(180deg);" : null)}

    path {
      fill: ${props => props.arrowColor};
    }
    ${props =>
      props.flip ? "margin-left: 0 !important;" : "margin-right: 0 !important;"}
  }

  &:hover {
    svg {
      ${media.fullWidth} {
        ${props => (props.flip ? "margin-right: 15px;" : "margin-left: 15px;")}
      }
      ${media.desktop} {
        ${props =>
          props.flip ? "margin-right: 0.904vw;" : "margin-left: 0.904vw;"}
      }
      ${media.tablet} {
        ${props =>
          props.flip ? "margin-right: 1.465vw;" : "margin-left: 1.465vw;"}
      }
      ${media.mobile} {
        ${props => (props.flip ? "margin-right: 4vw;" : "margin-left: 4vw;")}
      }
      transition-timing-function: ${easing.button};
      transition-duration: 300ms;
    }
  }
`
const Row = styled.div<{ flip?: boolean; lineColor?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid ${({ lineColor }) => lineColor};
  border-image-slice: 1;
  border-image-source: ${props => props.lineColor};

  ${media.fullWidth} {
    padding-bottom: 5px;
    border-width: 1px;
  }
  ${media.desktop} {
    padding-bottom: 0.301vw;
    border-width: 0.06vw;
  }
  ${media.tablet} {
    padding-bottom: 0.488vw;
    border-width: 0.098vw;
  }
  ${media.mobile} {
    padding-bottom: 1.333vw;
    border-width: 0.267vw;
  }

  ${props => (props.flip ? "flex-direction: row-reverse;" : null)}
`

const Text = styled.div<{ textColor?: string }>`
  ${text.buttonL}

  background: ${props => props.textColor};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  display: inline-block;

  &::after {
    content: "";
    display: block;
    width: 100%;
    height: 2px; 
    background: ${props => props.textColor}; 
    position: absolute;
    bottom: -2px; 
    left: 0;
    transition: all 0.3s ease-in-out;
  }
`

